/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import history from "../../../../@history";
import PageModel from "../model/PageModel";
import { showMessage } from "../../../store/BASE/messageSlice";

export const getPage = createAsyncThunk(
  "pages/getPage",
  async (id, { dispatch, getState }) => {
    try {
      // send request to get page
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/pages/${id}`
      );
      // get response data
      const data = await response.data;
      // return data
      return data;
    } catch (error) {
      // if error, redirect to pages page
      history.push({ pathname: `/pages` });
      // show error message
      dispatch(showMessage({ message: "Page Not Found", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const getFullPage = createAsyncThunk(
  "pages/getFullPage",
  async (id, { dispatch, getState }) => {
    try {
      // send request to get page
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/pages/${id}`
      );
      // define response data
      const data = await response.data;
      // get course data
      const courseResponse = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/courses/${data.course_id}`
      );
      // define course data
      const courseData = await courseResponse.data;
      // get the lesson data
      const lessonResponse = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/lessons/${data.lesson_id}`
      );
      // define lesson data
      const lessonData = await lessonResponse.data;
      // get the section data
      const sectionResponse = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/sections/${data.section_id}`
      );
      // define section data
      const sectionData = await sectionResponse.data;
      // add extra data to the response
      data.course = courseData;
      data.lesson = lessonData;
      data.section = sectionData;

      // return data
      return data;

    } catch (error) {
      // if error, redirect to pages page
      history.push({ pathname: `/pages` });
      // show error message
      dispatch(showMessage({ message: "Page Not Found", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const addPage = createAsyncThunk(
  "pages/addPage",
  async (page, { dispatch, getState }) => {
    try {
      // set user id
      page.user_id = getState().user.id;
      // send request to add page
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/pages`,
        page
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Page Added" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "Page Not Added", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const updatePage = createAsyncThunk(
  "pages/updatePage",
  async (page, { dispatch, getState }) => {
    try {
      // set user id
      page.user_id = getState().user.id;
      // send request to update page
      const response = await axios.patch(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/pages/${page.id}`,
        page
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Page Updated" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "Page Not Updated", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const removePage = createAsyncThunk(
  "pages/removePage",
  async (id, { dispatch, getState }) => {
    try {
      // send request to remove page
      const response = await axios.delete(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/pages/${id}`
      );
      // get response data
      await response.data;
      // show success message
      dispatch(showMessage({ message: "Page Removed" }));
      // return data
      return id;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "Page Not Removed", variant: "error" }));
      // return null
      return null;
    }
  }
);

// define selector
export const selectPage = ({ pages }) => pages.instance;

export const selectFullPage = ({ pages }) => pages.instance;

// thunks to update parts of page
export const storePageBasicInfo = createAsyncThunk(
  "template/storePageBasicInfo",
  async (data) => {
    return data;
  }
);

export const storePageTemplateSelection = createAsyncThunk(
  "template/storePageTemplateSelection",
  async (data) => {
    return data;
  }
);

export const storePageContent = createAsyncThunk(
  "template/storePageContent",
  async (data) => {
    return data;
  }
);

// define slice
const pageSlice = createSlice({
  name: "page",
  initialState: null,
  reducers: {
    newPage: (state, action) => PageModel(),
    resetPage: () => null,
  },
  extraReducers: {
    [getPage.pending]: (state, action) => null,
    [getPage.fulfilled]: (state, action) => action.payload,
    [getFullPage.pending]: (state, action) => null,
    [getFullPage.fulfilled]: (state, action) => action.payload,
    [updatePage.fulfilled]: (state, action) => action.payload,
    [removePage.fulfilled]: (state, action) => null,
    [storePageBasicInfo.fulfilled]: (state, action) => {
      state.course_id = action.payload.course_id;
      state.lesson_id = action.payload.lesson_id;
      state.section_id = action.payload.section_id;
      state.title = action.payload.title;
      state.label = action.payload.label;
      state.screen = action.payload.screen;
      state.back = action.payload.back;
      state.next = action.payload.next;
    },
    [storePageTemplateSelection.fulfilled]: (state, action) => {
      state.template_id = action.payload.template_id;
    },
    [storePageContent.fulfilled]: (state, action) => {
      state.content = action.payload.content;
    },
  },
});

// export actions
export const { resetPage, newPage } = pageSlice.actions;

export default pageSlice.reducer;
